import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './ContractorRatings.module.css';
import Footer from '../../../components/common/Footer/Footer';

const ContractorRatings = () => {
  const navigate = useNavigate();

  const handleCreateComparison = () => {
    const isAuthenticated = localStorage.getItem('token');
    if (isAuthenticated) {
      navigate('/homeowner/create-comparison');
    } else {
      navigate('/homeowner/login');
    }
  };

  const [activeFeature, setActiveFeature] = useState(null);



  const features = [
    {
      id: 'communication',
      icon: 'fas fa-comments',
      title: 'Communication & Professionalism',
      description: 'We evaluate how well contractors communicate in their proposals, their accountability measures, and professional standards.',
      benefits: [
        'Clear contact information and availability',
        'Professional communication style',
        'Accountability and responsibility measures',
        'Dispute resolution approach'
      ]
    },
    {
      id: 'documentation',
      icon: 'fas fa-file-alt',
      title: 'Documentation Quality',
      description: 'Assessment of how well contractors document their work scope, specifications, and project management approach.',
      benefits: [
        'Detailed project specifications',
        'Clear scope of work documentation',
        'Professional proposal presentation',
        'Comprehensive project planning'
      ]
    },
    {
      id: 'safety',
      icon: 'fas fa-shield-alt',
      title: 'Safety & Compliance',
      description: 'Evaluation of contractor safety practices, insurance coverage, licensing, and regulatory compliance standards.',
      benefits: [
        'Proper insurance coverage',
        'Valid licensing and certifications',
        'Safety protocol adherence',
        'Regulatory compliance measures'
      ]
    },
    {
      id: 'reliability',
      icon: 'fas fa-clock',
      title: 'Timeline & Reliability',
      description: 'Assessment of contractor reliability through their timeline planning, project management approach, and execution strategies.',
      benefits: [
        'Realistic timeline planning',
        'Clear project milestones',
        'Contingency planning approach',
        'Proposal quality indicators'
      ]
    }
  ];



  const ratingCriteria = [
    {
      title: 'Communication & Accountability',
      description: 'Evaluates contact information, site access conditions, work hours, and debris handling practices.',
      icon: 'fas fa-comments',
      factors: ['Point of contact clarity', 'Site access requirements', 'Work hours specification', 'Cleanliness protocols']
    },
    {
      title: 'Documentation & Recordkeeping',
      description: 'Assesses proposal structure, language clarity, professionalism, and record maintenance plans.',
      icon: 'fas fa-file-text',
      factors: ['Proposal organization', 'Technical clarity', 'Professional presentation', 'Documentation plans']
    },
    {
      title: 'Timeline & Schedule Factors',
      description: 'Analyzes schedule clarity, timeline realism, and delay management strategies.',
      icon: 'fas fa-calendar-alt',
      factors: ['Schedule definition', 'Timeline feasibility', 'Milestone clarity', 'Delay contingency']
    },
    {
      title: 'Scope & Methodology',
      description: 'Reviews construction methods, project sequencing, and property protection measures.',
      icon: 'fas fa-tools',
      factors: ['Construction methods', 'Project phases', 'Material specifications', 'Site protection']
    },
    {
      title: 'Safety Measurements',
      description: 'Evaluates insurance coverage, warranty terms, liability clauses, and safety compliance.',
      icon: 'fas fa-shield-alt',
      factors: ['Insurance details', 'Warranty coverage', 'Liability terms', 'Safety protocols']
    },
    {
      title: 'Contracting Behavior',
      description: 'Analyzes quote validity, dispute resolution methods, and professional conduct indicators.',
      icon: 'fas fa-handshake',
      factors: ['Quote expiry terms', 'Dispute handling', 'Professional conduct', 'Contract clarity']
    }
  ];

  const ratingLevels = [
    {
      level: 'Excellent',
      score: '9-10',
      color: '#10b981',
      description: 'Top-tier contractors with exceptional proposal quality and professionalism',
      benefits: ['Comprehensive documentation', 'Clear communication style', 'Strong safety practices', 'Reliable timeline planning']
    },
    {
      level: 'Good',
      score: '7-8',
      color: '#3b82f6',
      description: 'Quality contractors with solid proposals and good professional standards',
      benefits: ['Adequate documentation', 'Professional communication', 'Basic safety measures', 'Reasonable timelines']
    },
    {
      level: 'Fair',
      score: '5-6',
      color: '#f59e0b',
      description: 'Average contractors with basic proposals that may need clarification',
      benefits: ['Limited documentation', 'Basic communication', 'Standard safety measures', 'General timelines']
    },
    {
      level: 'Poor',
      score: '0-4',
      color: '#ef4444',
      description: 'Contractors with inadequate proposals showing concerning gaps',
      benefits: ['Insufficient documentation', 'Poor communication', 'Safety concerns', 'Unrealistic timelines']
    }
  ];

  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroShapes}>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
          </div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1>Contractor Ratings</h1>
            <p className={styles.heroSubtitle}>
              Comprehensive contractor evaluations based on their proposal quality, communication style, and behavioral patterns
            </p>
            <p className={styles.heroDescription}>
              We rate contractors across multiple dimensions by analyzing the proposals you upload, evaluating their 
              communication quality, documentation standards, safety practices, and professional behavior to help you choose the right contractor.
            </p>

          </div>
          <div className={styles.heroVisual}>
            <div className={styles.ratingPreview}>
              <div className={styles.previewHeader}>
                <h4>Contractor Rating Profile</h4>
              </div>
              <div className={styles.previewContent}>
                <div className={styles.contractorInfo}>
                  <div className={styles.contractorAvatar}>
                    <i className="fas fa-user-tie"></i>
                  </div>
                  <div className={styles.contractorDetails}>
                    <h5>Elite Construction Co.</h5>
                    <div className={styles.ratingDisplay}>
                      <div className={styles.stars}>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star-half-alt"></i>
                      </div>
                      <span className={styles.ratingScore}>8.2/10</span>
                    </div>
                    <div className={styles.ratingBadge}>
                      <span className={styles.excellentBadge}>Good Rating</span>
                    </div>
                  </div>
                </div>
                <div className={styles.ratingMetrics}>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Communication</span>
                    <div className={styles.metricBar}>
                      <div className={styles.metricFill} style={{ width: '85%' }}></div>
                    </div>
                    <span className={styles.metricValue}>8.5</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Documentation</span>
                    <div className={styles.metricBar}>
                      <div className={styles.metricFill} style={{ width: '70%' }}></div>
                    </div>
                    <span className={styles.metricValue}>7.0</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Safety</span>
                    <div className={styles.metricBar}>
                      <div className={styles.metricFill} style={{ width: '90%' }}></div>
                    </div>
                    <span className={styles.metricValue}>9.0</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>What We Evaluate in Contractors</h2>
            <p>Comprehensive assessment of contractor capabilities based on their proposal quality and professional standards</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={feature.id} 
                className={`${styles.featureCard} ${activeFeature === feature.id ? styles.active : ''}`}
                onMouseEnter={() => setActiveFeature(feature.id)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className={styles.featureIcon}>
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className={styles.featureBenefits}>
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className={styles.benefit}>
                      <i className="fas fa-check"></i>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rating Criteria Section */}
      <section className={styles.criteriaSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Contractor Rating Criteria</h2>
            <p>Key performance areas we evaluate to rate contractors based on their proposal quality and professional standards</p>
          </div>
          <div className={styles.criteriaGrid}>
            {ratingCriteria.map((criteria, index) => (
              <div key={index} className={styles.criteriaCard}>
                <div className={styles.criteriaHeader}>
                  <div className={styles.criteriaIcon}>
                    <i className={criteria.icon}></i>
                  </div>
                  <div className={styles.criteriaInfo}>
                    <h3>{criteria.title}</h3>
                  </div>
                </div>
                <p>{criteria.description}</p>
                <div className={styles.criteriaFactors}>
                  {criteria.factors.map((factor, idx) => (
                    <span key={idx} className={styles.factor}>
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rating Levels Section */}
      <section className={styles.levelsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Contractor Rating Levels</h2>
            <p>Understanding our rating system to identify high-quality contractors for your project</p>
          </div>
          <div className={styles.levelsGrid}>
            {ratingLevels.map((level, index) => (
              <div key={index} className={styles.levelCard}>
                <div className={styles.levelHeader}>
                  <div className={styles.levelBadge} style={{ background: level.color }}>
                    {level.level}
                  </div>
                  <div className={styles.levelScore}>{level.score}</div>
                </div>
                <h3>{level.description}</h3>
                <div className={styles.levelBenefits}>
                  {level.benefits.map((benefit, idx) => (
                    <div key={idx} className={styles.levelBenefit}>
                      <i className="fas fa-check-circle"></i>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsContent}>
            <div className={styles.benefitsText}>
              <h2>Why Contractor Ratings Matter</h2>
              <div className={styles.benefitsList}>
                <div className={styles.benefitItem}>
                  <i className="fas fa-shield-alt"></i>
                  <div>
                    <h4>Quality Assurance</h4>
                    <p>Choose contractors with proven documentation standards and professional practices for better project outcomes</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-user-check"></i>
                  <div>
                    <h4>Professional Standards</h4>
                    <p>Identify contractors who demonstrate clear communication, accountability, and professional behavior</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-clock"></i>
                  <div>
                    <h4>Reliable Planning</h4>
                    <p>Select contractors who show realistic timeline planning and proper project management skills</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-balance-scale"></i>
                  <div>
                    <h4>Informed Decisions</h4>
                    <p>Compare contractors objectively based on their proposal quality and professional capabilities</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.benefitsVisual}>
              <div className={styles.statsCard}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>10</span>
                  <span className={styles.statLabel}>Rating Criteria</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>4</span>
                  <span className={styles.statLabel}>Rating Levels</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>100%</span>
                  <span className={styles.statLabel}>Objective Evaluation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2>Get Contractor Ratings Today</h2>
            <p>Upload your contractor proposals to get comprehensive ratings and find the best contractors for your project</p>
            <div className={styles.ctaButtons}>
              <button className={styles.primaryButton} onClick={handleCreateComparison}>
                <i className="fas fa-star"></i>
                View Contractor Ratings
              </button>
              <button className={styles.secondaryButton} onClick={() => navigate('/homeowner/pricing')}>
                <i className="fas fa-info-circle"></i>
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContractorRatings; 